/**
 * Iter 67du — Curated destinations carousel.
 *
 * Replaces the previous country-grouped pill grid (TopLocationDestinations).
 * User wanted a single horizontal carousel ordered by intent:
 *   1) Main airports (Tirana, Vlora, Prishtine)
 *   2) Headline holiday destinations (Saranda, Durres, Ksamil)
 *   3) Other notable Balkan locations from the live landing index
 *
 * Each card is a Link to `/car-rental/<slug>` so SEO bots still crawl the
 * full landing-page graph; visually the user sees a sleek horizontal scroll
 * with snap-to-card behaviour.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { MapPin, Plane, Waves, Compass, ChevronLeft, ChevronRight } from "lucide-react";

// Ordered intent buckets — these names get matched (case-insensitive,
// accent-folded) against the live `/locations/landing-index` payload so we
// always link to a real landing page. If a bucket entry isn't seeded yet,
// we skip it.
const FEATURED_AIRPORTS = ["Tirana Airport", "Vlora Airport", "Pristina Airport"];
const FEATURED_HOLIDAYS = ["Sarande", "Durres", "Ksamil", "Vlore", "Himare", "Berat"];
const FEATURED_OTHER    = ["Tirana", "Pristina", "Shkodra", "Gjirokastra", "Korca", "Pogradec", "Skopje", "Thessaloniki", "Athens", "Ohrid", "Budva"];

// Strip diacritics (Vlorë → Vlore, Sarandë → Sarande, Durrës → Durres) so
// our feature list — which uses ASCII — matches the seeded catalog rows
// regardless of their native spelling.
function _normalize(s) {
  return (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

function _kindIcon(kind) {
  if (kind === "airport") return Plane;
  if (kind === "port" || kind === "tourist") return Waves;
  return Compass;
}

function _kindTone(kind) {
  if (kind === "airport") return { bg: "bg-amber-50", ring: "ring-amber-200", text: "text-amber-700", chip: "Airport" };
  if (kind === "tourist") return { bg: "bg-sky-50", ring: "ring-sky-200", text: "text-sky-700", chip: "Holiday" };
  if (kind === "port")    return { bg: "bg-cyan-50", ring: "ring-cyan-200", text: "text-cyan-700", chip: "Port" };
  return { bg: "bg-neutral-50", ring: "ring-neutral-200", text: "text-neutral-700", chip: "City" };
}

export default function TopLocationDestinations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const railRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/locations/landing-index")
      .then((r) => { if (alive) setRows(r.data || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Build the ordered list by matching against the live index.
  const carouselItems = useMemo(() => {
    if (!rows.length) return [];
    // Accent-folded lookup so "Vlora" matches "Vlorë", "Saranda" → "Sarandë",
    // "Pristina"/"Prishtina"/"Prishtinë" all resolve to the same row.
    const lookup = new Map();
    for (const r of rows) {
      const keys = [r.name, ...(r.aliases || [])];
      for (const k of keys) {
        const n = _normalize(k);
        if (n && !lookup.has(n)) lookup.set(n, r);
      }
    }
    const picked = new Set();
    const out = [];
    const pushIfFound = (name) => {
      const hit = lookup.get(_normalize(name));
      if (hit && !picked.has(hit.slug)) {
        picked.add(hit.slug);
        out.push(hit);
      }
    };
    FEATURED_AIRPORTS.forEach(pushIfFound);
    FEATURED_HOLIDAYS.forEach(pushIfFound);
    FEATURED_OTHER.forEach(pushIfFound);
    // Fill the tail with any remaining landing-indexed locations so the
    // carousel never feels empty even if the curated names mis-match.
    for (const r of rows) {
      if (!picked.has(r.slug)) {
        picked.add(r.slug);
        out.push(r);
      }
      if (out.length >= 24) break;
    }
    return out;
  }, [rows]);

  const scrollBy = (delta) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  if (loading || carouselItems.length === 0) return null;

  return (
    <section
      className="bg-white py-16 border-t border-border"
      data-testid="top-location-destinations"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-7 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-amber-700 font-bold mb-3 flex items-center gap-1.5">
              <MapPin className="h-3 w-3" /> Top destinations
            </div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium">
              Drive from <span className="text-amber-600">airports</span> to the <span className="text-sky-600">coast</span>
            </h2>
            <p className="mt-2 text-neutral-600 text-sm max-w-xl">
              Curated pick of main airports and Balkan holiday hotspots — tap any to see local hosts.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-360)}
              aria-label="Scroll destinations left"
              className="h-9 w-9 rounded-full bg-white border border-border shadow-sm hover:bg-neutral-50 hover:border-amber-300 flex items-center justify-center transition"
              data-testid="top-destinations-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(360)}
              aria-label="Scroll destinations right"
              className="h-9 w-9 rounded-full bg-white border border-border shadow-sm hover:bg-neutral-50 hover:border-amber-300 flex items-center justify-center transition"
              data-testid="top-destinations-next"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          ref={railRef}
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 scrollbar-hide"
          data-testid="top-destinations-rail"
        >
          {carouselItems.map((loc) => {
            const Icon = _kindIcon(loc.kind);
            const tone = _kindTone(loc.kind);
            return (
              <Link
                key={loc.slug}
                to={`/car-rental/${loc.slug}`}
                className={`group shrink-0 w-[180px] sm:w-[200px] rounded-2xl border border-border ${tone.bg} hover:shadow-md hover:-translate-y-0.5 transition snap-start`}
                data-testid={`top-destination-card-${loc.slug}`}
              >
                <div className="p-4 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`h-10 w-10 rounded-xl bg-white ring-1 ${tone.ring} flex items-center justify-center ${tone.text} group-hover:scale-105 transition`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/80 ring-1 ${tone.ring} ${tone.text}`}>
                      {tone.chip}
                    </span>
                  </div>
                  <div className="font-display text-base font-semibold text-neutral-900 leading-tight">{loc.name}</div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">{loc.country}</div>
                  <div className="mt-auto pt-3 text-[12px] font-semibold text-amber-700 group-hover:text-amber-900 inline-flex items-center gap-1">
                    Rent here <ChevronRight className="h-3 w-3" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
